import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Sign a single image URL
export async function signImageUrl(imageUrl: string): Promise<string> {
  if (!imageUrl || typeof imageUrl !== 'string') return imageUrl
  
  // Only sign URLs that need signing (from our backend)
  if (!imageUrl.startsWith('/outputs/extracted_images/')) {
    return imageUrl
  }

  // Attach Authorization header if token exists
  let token: string | null = null
  try {
    const userJson = localStorage.getItem('user')
    if (userJson) {
      const user = JSON.parse(userJson)
      token = user?.token || null
    }
  } catch {}

  try {
    const res = await fetch(`/api/images/signed-batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      },
      body: JSON.stringify({ paths: [imageUrl], ttl: 900 })
    })
    
    if (!res.ok) return imageUrl
    
    const data = await res.json()
    const map: Record<string, string> = data.urls || {}
    
    return map[imageUrl] || imageUrl
  } catch {
    return imageUrl
  }
}

// Replace canonical image src with signed URLs using backend signer
export async function signImageUrlsInHtml(html: string): Promise<string> {
  if (!html || typeof html !== 'string') return html
  const imgSrcRegex = /<img\s+[^>]*src=["']([^"']+)["'][^>]*>/gi
  const paths: string[] = []

  let m
  while ((m = imgSrcRegex.exec(html)) !== null) {
    const src = m[1]
    if (src.startsWith('/outputs/extracted_images/')) {
      paths.push(src)
    }
  }

  if (paths.length === 0) return html

  // Attach Authorization header if token exists
  let token: string | null = null
  try {
    const userJson = localStorage.getItem('user')
    if (userJson) {
      const user = JSON.parse(userJson)
      token = user?.token || null
    }
  } catch {}

  const res = await fetch(`/api/images/signed-batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    },
    body: JSON.stringify({ paths, ttl: 900 })
  })
  if (!res.ok) return html
  const data = await res.json()
  const map: Record<string, string> = data.urls || {}

  // Replace occurrences globally per path
  let out = html
  for (const p of Object.keys(map)) {
    const signedUrl = map[p]
    out = out.split(`src="${p}"`).join(`src="${signedUrl}"`).split(`src='${p}'`).join(`src='${signedUrl}'`)
  }
  return out
}
